<template>
    <Modal
        class="capping-pacing-modal"
        showHeaderSeparator
        :version="1"
        okButtonText="Apply"
        title="Edit Capping and Pacing"
        v-on="listeners"
        v-bind="$props"
    >
        <div slot="modal-body">
            <div class="content-row">
                <label class="font-4">Placement</label>
                <span>{{ adUnitName }} | {{ placementName }}</span>
            </div>
            <div class="content-row">
                <label class="font-4">Ad Delivery</label>
                <div>
                    <Switcher
                        class="switcher"
                        uniqueId="segmentCappingPacingAdDeliverySwitcher"
                        :isActive="deliveryEnabled"
                        :isDisabled="forceAdDelivery"
                        text="on"
                        textInactive="off"
                        @changed="deliveryEnabled = $event"
                    />
                    <span class="font-6" v-if="!deliveryEnabled">
                        (No ads will be delivered for this placement)
                    </span>
                    <span class="font-6" v-if="forceAdDelivery">
                        (Default placement can't be turned off)
                    </span>
                </div>
            </div>
            <section>
                <label :class="{disabled: !deliveryEnabled}">
                    <input type="checkbox"
                           id="segmentCappingEnabledToggle"
                           v-model="capping.enabled"
                           :disabled="!deliveryEnabled" />
                    Capping
                </label>
                <div>
                    Limit to
                    <input type="number"
                           id="segmentCappingLimit"
                           class="small"
                           step="1"
                           min="1"
                           v-model.number="$v.capping.maxImpressions.$model"
                           :disabled="!deliveryEnabled || !capping.enabled" />
                    Impressions /
                    <Dropdown
                        class="units-dropdown"
                        uniqueId="segmentCappingUnit"
                        :isDisable="!deliveryEnabled || !capping.enabled"
                        :selectedId="capping.unit || 'd'"
                        :options="unitOptions"
                        :searchEnabled="false"
                        @dropdownSelectedItemChanged="cappingUnitChanged"
                        height="27"
                    />
                    <span class="placement-defaults" v-if="defaultCapping.maxImpressions">
                        (Default: {{defaultCapping.maxImpressions}}/{{defaultCapping.unit === 'd' ? 'Day' : 'Hour'}})
                    </span>
                </div>
                <div class="error-message"
                     v-if="$v.capping.maxImpressions.$dirty && $v.capping.maxImpressions.required">
                    Number of impressions is a mandatory field.
                </div>
                <div class="error-message"
                     v-else-if="$v.capping.maxImpressions.$dirty && $v.capping.maxImpressions.greaterThanZero">
                    Number of impressions should be greater than 0.
                </div>
                <div class="error-message"
                     v-else-if="$v.capping.maxImpressions.$dirty && $v.capping.maxImpressions.lessThan1000">
                    Number of impressions should be less than or equal to 1000.
                </div>
                <div class="error-message"
                     v-else-if="$v.capping.maxImpressions.$dirty && $v.capping.maxImpressions.notInt">
                    Number of impressions should be higher than 0, whole number and less than 1000.
                </div>
            </section>
            <section>
                <label :class="{disabled: !deliveryEnabled}">
                    <input type="checkbox"
                           id="segmentPacingEnabledToggle"
                           v-model="pacing.enabled"
                           :disabled="!deliveryEnabled" />
                    Pacing
                </label>
                <div>
                    Show a maximum of 1 ad /
                    <input type="number"
                           id="segmentPacingLimit"
                           class="small"
                           step="0.1"
                           min="0.1"
                           v-model.number="$v.pacing.numOfMinutes.$model"
                           :disabled="!deliveryEnabled || !pacing.enabled" />
                    minutes
                    <span class="placement-defaults" v-if="defaultPacing.numOfMinutes">
                        (Default: 1 Ad/{{defaultPacing.numOfMinutes.toFixed(1)}}min)
                    </span>
                </div>
                <div class="error-message" v-if="$v.pacing.numOfMinutes.$dirty && $v.pacing.numOfMinutes.required">
                    Number of minutes is a mandatory field
                </div>
                <div class="error-message"
                     v-if="$v.pacing.numOfMinutes.$dirty && $v.pacing.numOfMinutes.between
                      && !$v.pacing.numOfMinutes.required">
                    Number of minutes should be greater than 0 and less than 1000, <br />
                    to the nearest tenth after the comma.
                </div>
            </section>
        </div>
        <div slot="modal-footer-content" class="footer-content">
            <button class="important"
                    id="revertToDefaultButton"
                    :class="{disabled: !fieldsChanged}"
                    @click="revertClick">
                Revert to Default
            </button>
            <span class="footer-error error-message font-7" v-if="hasErrors && touched">
                Please complete all fields correctly
            </span>
        </div>
    </Modal>
</template>

<script>
import Modal from 'components/modals/Modal.vue';
import Switcher from 'components/Switcher.vue';
import consts from 'services/metaData/consts';
import Dropdown from 'components/dropdowns/Dropdown.vue';
import {
    isEmpty, isEqual, omitBy, isUndefined
} from 'lodash';

export default {
    name: 'CappingPacingModal',
    components: { Dropdown, Modal, Switcher },
    props: {
        ...Modal.props,
        adUnit: {
            required: true,
            validator: (val) => Object.keys(consts.ADUNIT_TO_NAME).includes(val)
        },
        placementId: {
            type: Number,
            required: true
        },
        placementName: {
            type: String,
            required: true
        },
        forceAdDelivery: {
            type: Boolean,
            default: false
        },
        adDelivery: {
            type: Boolean,
            default: true
        },
        placement: Object,
        defaultCapping: Object,
        currentCapping: Object,
        defaultPacing: Object,
        currentPacing: Object
    },
    data() {
        return {
            touched: false,
            deliveryEnabled: true,
            capping: {
                enabled: false,
                unit: 'd',
                maxImpressions: null
            },
            pacing: {
                enabled: false,
                numOfMinutes: null
            }
        };
    },
    computed: {
        componentId() {
            return `capping-pacing-modal-${this.id || Math.random().toString().substring(2, 7)}`;
        },
        listeners() {
            return { ...this.$listeners, ok: this.emitOk };
        },
        adUnitName() {
            return consts.ADUNIT_TO_NAME[this.adUnit];
        },
        unitOptions() {
            return Object.keys(consts.UNIT_NAMES).map((key) => ({ id: key, displayText: consts.UNIT_NAMES[key] }));
        },
        fieldsChanged() {
            return !isEqual(this.capping, this.defaultCapping) || !isEqual(this.pacing, this.defaultPacing)
                || !isEqual(this.deliveryEnabled, this.placement.delivery);
        },
        hasErrors() {
            if (!this.$v?.capping || !this.$v?.pacing) return false;

            return [
                this.$v.capping.maxImpressions.required,
                this.$v.capping.maxImpressions.lessThan1000,
                this.$v.capping.maxImpressions.greaterThanZero,
                this.$v.capping.maxImpressions.notInt,
                this.$v.pacing.numOfMinutes.required,
                this.$v.pacing.numOfMinutes.between
            ].some(Boolean);
        },
        consts: () => consts
    },
    methods: {
        revertClick() {
            if (!this.fieldsChanged) return;
            this.$emit('ok', { reverted: true });
        },
        emitOk() {
            this.$v.$touch();

            this.touched = true;

            if (this.hasErrors) return;

            const cappingEnabled = this.capping.enabled;
            const cappingEnabledChanged = Boolean(this.capping.enabled) !== Boolean(this.placement.capping.enabled);
            const maxImpressionsChanged = this.capping.maxImpressions !== this.placement.capping.maxImpressions;
            const unitChanged = this.capping.unit !== this.placement.capping.unit;
            const cappingChanged = cappingEnabledChanged || ((maxImpressionsChanged || unitChanged) && cappingEnabled);
            const unit = this.capping.unit || 'd';

            const capping = omitBy({
                enabled: cappingChanged ? cappingEnabled : undefined,
                unit: (cappingEnabled && cappingChanged)
                    ? unit : undefined,
                maxImpressions: (cappingEnabled && cappingChanged) ? this.capping.maxImpressions : undefined
            }, isUndefined);
            const pacingEnabled = this.pacing.enabled;
            const pacingEnabledChanged = Boolean(this.pacing.enabled) !== Boolean(this.placement.pacing.enabled);
            const numOfMinutesChanged = this.pacing.numOfMinutes !== this.placement.pacing.numOfMinutes;
            const pacingChanged = pacingEnabledChanged || (numOfMinutesChanged && pacingEnabled);

            const pacing = omitBy({
                enabled: pacingChanged ? pacingEnabled : undefined,
                numOfMinutes: (pacingChanged && pacingEnabled) ? this.pacing.numOfMinutes : undefined
            }, isUndefined);

            const eventData = {
                adDelivery: this.deliveryEnabled,
                capping,
                pacing
            };
            this.$emit('ok', eventData);
        },
        resetFields() {
            this.deliveryEnabled = this.forceAdDelivery || this.adDelivery;
            this.capping = isEmpty(this.currentCapping)
                ? { ...this.defaultCapping } : { ...this.capping, ...this.currentCapping };
            this.pacing = isEmpty(this.currentPacing)
                ? { ...this.defaultPacing } : { ...this.pacing, ...this.currentPacing };
        },
        cappingUnitChanged({ id }) {
            this.capping.unit = id;
        }
    },
    mounted() {
        this.resetFields();
    },
    watch: {
        open() {
            this.resetFields();
        }
    },
    validations: {
        capping: {
            maxImpressions: {
                required() {
                    return this.capping.enabled && !this.capping.maxImpressions && this.capping.maxImpressions !== 0;
                },
                greaterThanZero() {
                    return this.capping.enabled && this.capping.maxImpressions < 1;
                },
                lessThan1000() {
                    return this.capping.enabled && this.capping.maxImpressions > 1000;
                },
                notInt() {
                    return this.capping.enabled && !!this.capping.maxImpressions
                        && !Number.isInteger(this.capping.maxImpressions);
                }
            }
        },
        pacing: {
            numOfMinutes: {
                required() {
                    return this.pacing.enabled && !this.pacing.numOfMinutes && this.pacing.numOfMinutes !== 0;
                },
                between() {
                    const numOfMinutes = Number(this.pacing.numOfMinutes);
                    const isNumber = !Number.isNaN(numOfMinutes);
                    const [, digitsAfterDecimal] = numOfMinutes.toString().split('.');
                    const isToTheTenth = !digitsAfterDecimal || digitsAfterDecimal.length <= 1;

                    return this.pacing.enabled
                        && (!isNumber || !isToTheTenth || numOfMinutes <= 0 || numOfMinutes >= 1000);
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";
@import "~styles/input.scss";
@import "~styles/buttons.scss";
@import "~styles/checkbox.scss";

.content-row {
    display: grid;
    grid-template-columns: 2fr 10fr;
    grid-column-gap: 3px;
    align-items: center;
    margin-top: 25px;
    margin-bottom: 20px;

    input.small {
        width: 65px;
        padding-right: 0;
    }

    // adjust switcher label position
    .switcher ::v-deep label.inputText {
        margin-left: -4px;
        margin-right: 5px;
    }
}

section {
    margin-top: 15px;
    border: $border;
    border-radius: $borderRadius;
    padding: 13px 15px 15px 15px;

    label {
        cursor: pointer;
        display: flex;
        margin-bottom: 18px;
        align-items: center;
        input[type="checkbox"] {
            margin-left: 0;
            margin-top: 2px;
            margin-right: 10px;
        }
    }

    input[type='number'] {
        width: 70px;
        padding-right: 0;
    }

    .units-dropdown {
        width: 82px;
    }
}

.footer-content {
    padding-left: 25px;
    flex: 1;
}

.disabled {
    color: $light-800;
}

// Modal component style overrides
.capping-pacing-modal ::v-deep {

    div.modal-container {
        width: 620px;
    }

    .modal-header {
        padding-top: 15px;
    }

    .modal-body {
        margin: 0 16px 20px 18px;
        padding: 0;
    }
}

.error-message {
    color: $negative;
    line-height: 20px;
    margin-top: 15px;

    &.footer-error {
        margin-top: unset;
        margin-left: 10px;
    }
}

.placement-defaults {
    color: $light-800;
    margin-left: 10px;
}
</style>
